import type { PageVideo } from '~/modules/seo/util/collect-module-videos';

interface ProductGalleryVideo {
    Title?: string | null;
    VideoLink?: string | null;
    CreatedDate?: string | null;
    ThumbnailOfficial?: string | null;
    ThumbnailImageQuick?: string | null;
    LibraryImage?: {
        LibraryImageFile?: {
            VideoCover: { Desktop?: string | null };
        } | null;
    } | null;
}

/** Makes a site-relative asset path absolute; third-party thumbnails are already absolute. */
const toAbsolute = (url: string, baseUrl: string): string =>
    url.startsWith('/') ? baseUrl + url : url;

/**
 * Pull the product gallery's videos into the shape structured data needs.
 *
 * Product videos are YouTube or Vimeo embeds held against the product's library, so the player link
 * comes from the backend (`VideoLink`) rather than being rebuilt here. The professional's own cover
 * image wins over the platform thumbnail because it is the frame they chose to represent the video.
 */
export const collectProductVideos = (
    videos: ReadonlyArray<ProductGalleryVideo> | null | undefined,
    baseUrl: string,
): PageVideo[] =>
    (videos ?? []).flatMap((video) => {
        const url = video.VideoLink?.trim();
        const thumbnail =
            video.LibraryImage?.LibraryImageFile?.VideoCover?.Desktop ??
            video.ThumbnailOfficial ??
            video.ThumbnailImageQuick;

        if (!url) {
            return [];
        }

        return [
            {
                url,
                title: video.Title ?? undefined,
                thumbnailUrl: thumbnail
                    ? toAbsolute(thumbnail, baseUrl)
                    : undefined,
                dateCreated: video.CreatedDate ?? undefined,
            },
        ];
    });
